import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';

// HOC
import Guard from '@/hoc/Guard';

// COMPONENTS
import AppsCarousel from '@/components/Carousels/AppsCarousel';
import MyAppsCarousel from '@/components/Carousels/MyAppsCarousel';
import ShowsMoviesCarousel from '@/components/Carousels/ShowsMoviesCarousel';
import EpisodesCarousel from '@/components/Carousels/EpisodesCarousel';
import ServicesCarousel from '@/components/Carousels/ServicesCarousel';
import InternetOption from '@/components/Cards/InternetOption';
import BannerPromo from '@/components/Banners/BannerPromo';
import BannerDynamic from '@/components/Banners/BannerDynamic';

// HELP
import BannerApp from './help/BannerApp';
import BannerAppFeatures from './help/BannerAppFeatures';
import BannerFeaturedContent from './help/BannerFeaturedContent';
import BannerLiveTV from './help/BannerLiveTV';
import BannerWithChannels from './help/BannerWithChannels';
import BannerAddApps from './help/BannerAddApps';
import BannerWatchNextPromo from './help/BannerWatchNextPromo';
import BannerFMB from './help/BannerFMB';
import BannerSaveYourApps from './help/BannerSaveYourApps';
import BannerSlingPromo from './help/BannerSlingPromo';
import MultipleBanners from './help/MultipleBanners';

// STYLES
import { Wrapper } from './styles';
import SectionTitleLink from '@/components/SectionTitleLink/SectionTitleLink';

const SwimlanesHOC = props => {
  const { data } = props;

  return React.useMemo(() => {
    if (!data) return null;

    return <Swimlanes {...props} />;
  }, [data]);
};

SwimlanesHOC.defaultProps = {
  data: {},
};

SwimlanesHOC.propTypes = {
  data: PropTypes.object,
};

const Swimlanes = props => {
  const {
    className,
    data,
    loadings,
    loadingType,
    loadingRows,
    analyticsLocation,
    handleUpdateContentStatus,
    handleUpdateAppStatus,
    onSelectClick,
    swimlanesShift,
    stylesType,
    hideTitle,
    hideMobileNav,
    cardType,
    viewType,
    bannerAddAppsProps,
  } = props;

  const [t] = useTranslation();
  const downXs = useMediaQuery(({ breakpoints }) => breakpoints.down('xs'));

  const updatedData = useMemo(() => {
    return Object.keys(data).length
      ? data
      : new Array(loadingRows).fill('').reduce((acc, item, index) => {
          acc[index] = { type: loadingType };
          return acc;
        }, {});
  }, [data]);

  const updatedLoadings = useMemo(() => {
    return Object.keys(loadings).length
      ? loadings
      : new Array(loadingRows).fill('').reduce((acc, item, index) => {
          acc[index] = true;
          return acc;
        }, {});
  }, [loadings]);

  const bannerTypes = [
    'banner_promo',
    'banner_dynamic',
    'banner_sling',
    'banner_sling_promo',
    'banner_sling_promo_peacock',
    'banner_hulu_live',
    'banner_live_tv',
    'banner_add_apps',
    'banner_binglelist',
    'banner_fmb_results',
    'banner_save_your_apps',
  ];

  return Object.keys(updatedData).map((key, index) => {
    const collection = updatedData[key];
    const loading = updatedLoadings[key];

    const title = collection.title || '';
    const swimlaneAnalytics = {
      swimlane_name: title,
      swimlane_id: collection.swimlane_list_id,
      swimlane_number_on_page: swimlanesShift + index + 1 || '',
    };
    const initialType = collection.type || loadingType;
    const type =
      loading || collection.data?.length || bannerTypes.includes(initialType)
        ? initialType
        : 'empty';
    const showSeeAll = title.toLowerCase().replace(/[^a-z]+/g, '') !== 'bingeworthy';

    switch (type) {
      case 'most_popular_on_app':
        return (
          <Guard partner={{ search_movies_shows: true }} key={index}>
            <Wrapper className={clsx('swimlane', className)}>
              <BannerFeaturedContent
                title={collection.featured_content_title}
                data={collection.featured_content}
              />
              <ShowsMoviesCarousel
                data={collection.data}
                title={title}
                link={showSeeAll ? collection.link : ''}
                loading={loading}
                analyticsLocation={analyticsLocation}
                onUpdateStatus={handleUpdateContentStatus}
                stylesType={stylesType}
                showSeeAll={showSeeAll}
                swimlaneAnalytics={swimlaneAnalytics}
                hideTitle={hideTitle}
                hideMobileNav={hideMobileNav}
              />
            </Wrapper>
          </Guard>
        );
      case 'content':
      case 'popular_on_app':
        return (
          <Guard partner={{ search_movies_shows: true }} key={index}>
            <Wrapper className={clsx('swimlane', className)}>
              <ShowsMoviesCarousel
                data={collection.data}
                title={title}
                link={showSeeAll ? collection.link : ''}
                loading={loading}
                analyticsLocation={analyticsLocation}
                onUpdateStatus={handleUpdateContentStatus}
                stylesType={stylesType}
                showSeeAll={showSeeAll}
                swimlaneAnalytics={swimlaneAnalytics}
                hideTitle={hideTitle}
                hideMobileNav={hideMobileNav}
              />
            </Wrapper>
          </Guard>
        );
      case 'multiple-content':
        return (
          <Guard partner={{ search_movies_shows: true }} key={index}>
            <Wrapper
              className={clsx(
                'swimlane swimlane--multiple-content swimlane--highlighted',
                className,
              )}
            >
              {!!title && !hideTitle && (
                <SectionTitleLink
                  className="swimlane__title"
                  title={title}
                  TypoProps={{ variant: 'h3' }}
                  // link={showSeeAll ? collection.link : ''}
                  analyticsLocation={analyticsLocation}
                />
              )}
              {collection.data.map((swimlane, idx) => {
                return (
                  <ShowsMoviesCarousel
                    data={swimlane.data}
                    title={swimlane.title}
                    link={showSeeAll ? swimlane.link : ''}
                    loading={loading}
                    analyticsLocation={analyticsLocation}
                    onUpdateStatus={handleUpdateContentStatus}
                    stylesType={stylesType}
                    showSeeAll={showSeeAll}
                    swimlaneAnalytics={swimlaneAnalytics}
                    hideTitle={hideTitle}
                    hideMobileNav={hideMobileNav}
                    key={idx}
                  />
                );
              })}
            </Wrapper>
          </Guard>
        );
      case 'episode':
        return (
          <Guard partner={{ search_movies_shows: true }} key={index}>
            <Wrapper className={clsx('swimlane', className)}>
              <EpisodesCarousel
                data={collection.data}
                title={title}
                link={showSeeAll ? collection.link : ''}
                loading={loading}
                analyticsLocation={analyticsLocation}
                stylesType={stylesType}
                swimlaneAnalytics={swimlaneAnalytics}
                hideTitle={hideTitle}
                hideMobileNav={hideMobileNav}
              />
            </Wrapper>
          </Guard>
        );
      case 'app_highlight':
        return (
          <Guard partner={{ use_marketplace: true }} key={index}>
            <Wrapper className={clsx('swimlane', 'swimlane--highlighted', className)}>
              <Typography className="swimlane__title" variant={downXs ? 'h3' : 'h2'}>
                {t('swimlanes.appHighlight.title')}
              </Typography>
              <BannerApp
                data={collection.app || {}}
                link={collection.link || ''}
                analyticsLocation={analyticsLocation}
              />
              <ShowsMoviesCarousel
                data={collection.data}
                title={title}
                link={collection.link || ''}
                loading={loading}
                analyticsLocation={analyticsLocation}
                onUpdateStatus={handleUpdateContentStatus}
                stylesType={stylesType}
                showSeeAll
                swimlaneAnalytics={swimlaneAnalytics}
                hideTitle={hideTitle}
                hideMobileNav={hideMobileNav}
              />
            </Wrapper>
          </Guard>
        );
      case 'app_features':
        return (
          <Guard partner={{ use_marketplace: true }} key={index}>
            <Wrapper className={clsx('swimlane', 'swimlane--app-features', className)}>
              {!!title && (
                <Typography className="m-b-15" variant="h3">
                  {title}
                </Typography>
              )}
              <BannerAppFeatures
                data={collection || {}}
                link={collection.link || ''}
                analyticsLocation={analyticsLocation}
              />
            </Wrapper>
          </Guard>
        );
      case 'app':
        return (
          <Guard partner={{ use_marketplace: true }} key={index}>
            <Wrapper className={clsx('swimlane', className)}>
              <AppsCarousel
                data={collection.data}
                title={title}
                link={collection.link || ''}
                loading={loading}
                analyticsLocation={analyticsLocation}
                onUpdateStatus={handleUpdateAppStatus}
                onRemoveStatus={handleUpdateAppStatus}
                onSelectClick={onSelectClick}
                stylesType={stylesType}
                showSeeAll
                swimlaneAnalytics={swimlaneAnalytics}
                hideTitle={hideTitle}
                hideMobileNav={hideMobileNav}
                cardType={cardType}
                viewType={viewType}
              />
            </Wrapper>
          </Guard>
        );
      case 'service':
        return (
          <Guard partner={{ use_marketplace: true }} key={index}>
            <Wrapper className={clsx('swimlane', className)}>
              <ServicesCarousel
                data={collection.data}
                loading={loading}
                analyticsLocation={analyticsLocation}
                swimlaneAnalytics={swimlaneAnalytics}
                link={collection.link || ''}
                showSeeAll
                hideMobileNav={hideMobileNav}
              />
            </Wrapper>
          </Guard>
        );
      case 'my_apps':
        return (
          <Guard key={index} partner={{ use_marketplace: true }}>
            <Wrapper className={clsx('swimlane', className)}>
              <MyAppsCarousel
                data={collection.data}
                title={title}
                link={collection.link || ''}
                analyticsLocation={analyticsLocation}
                onRemoveStatus={handleUpdateAppStatus}
                loading={loading}
              />
            </Wrapper>
          </Guard>
        );
      case 'internet_offer':
        return (
          <Guard key={index} partner={{ use_fmb: true }}>
            <Wrapper className={clsx('swimlane', className)}>
              <InternetOption
                data={collection.data[0] || []}
                analyticsLocation={analyticsLocation}
              />
            </Wrapper>
          </Guard>
        );
      case 'banner_promo':
        return (
          <Wrapper className={clsx('swimlane', className)} key={index}>
            <BannerPromo
              analyticsProps={{
                swimlane_id: collection.swimlane_list_id,
                swimlane_name: collection.title,
                link: collection.link,
              }}
              data={collection.data}
              link={collection.link || ''}
              analyticsLocation={analyticsLocation}
            />
          </Wrapper>
        );
      case 'banner_dynamic':
        return (
          <Wrapper className={clsx('swimlane', className)} key={index}>
            <BannerDynamic
              analyticsProps={{
                swimlane_id: collection.swimlane_list_id,
                swimlane_name: collection.title,
                link: collection.link,
              }}
              data={collection.data}
              link={collection.link || ''}
              analyticsLocation={analyticsLocation}
            />
          </Wrapper>
        );
      case 'multiple_banners':
        return (
          <Wrapper className={clsx('swimlane', className)} key={index}>
            <MultipleBanners
              analyticsProps={{
                swimlane_id: collection.swimlane_list_id,
                swimlane_name: collection.title,
              }}
              data={collection.data}
              analyticsLocation={analyticsLocation}
            />
          </Wrapper>
        );
      case 'banner_live_tv':
        return (
          <Guard partner={{ show_livetv: true }} key={index}>
            <Wrapper className={clsx('swimlane', className)}>
              <BannerLiveTV />
            </Wrapper>
          </Guard>
        );
      case 'banner_sling':
        return (
          <Guard partner={{ show_sling: true }} key={index}>
            <Wrapper className={clsx('swimlane', className)}>
              <BannerWithChannels />
            </Wrapper>
          </Guard>
        );
      case 'banner_sling_promo':
        return (
          <Guard partner={{ show_sling: true }} key={index}>
            <Wrapper className={clsx('swimlane', className)}>
              <BannerSlingPromo />
            </Wrapper>
          </Guard>
        );
      case 'banner_sling_promo_peacock':
        return (
          <Guard partner={{ show_sling: true }} key={index}>
            <Wrapper className={clsx('swimlane', className)}>
              <BannerWithChannels type="sling-promo-peacock" />
            </Wrapper>
          </Guard>
        );
      case 'banner_hulu_live':
        return (
          <Wrapper className={clsx('swimlane', className)} key={index}>
            <BannerWithChannels type="hulu-live" />
          </Wrapper>
        );
      case 'banner_add_apps':
        return (
          <Guard
            key={index}
            partner={{ use_marketplace: true }}
            selector={({ auth: { isAuth, user } }) => isAuth && !user?.content_count?.apps_have}
          >
            <Wrapper className={clsx('swimlane', className)}>
              <BannerAddApps {...bannerAddAppsProps} />
            </Wrapper>
          </Guard>
        );
      case 'banner_binglelist':
        return (
          <Guard
            key={index}
            selector={({ auth: { user }, watchNext }) =>
              !(user?.content_count?.watch_next_results > 0 || watchNext.wn_token)
            }
          >
            <Wrapper className={clsx('swimlane', className)}>
              <BannerWatchNextPromo analyticsLocation={analyticsLocation} />
            </Wrapper>
          </Guard>
        );
      case 'banner_fmb_results':
        return (
          <Guard key={index} partner={{ use_fmb: true }}>
            <Wrapper className={clsx('swimlane', className)}>
              <BannerFMB analyticsLocation={analyticsLocation} />
            </Wrapper>
          </Guard>
        );
      case 'banner_save_your_apps':
        return (
          <Guard
            partner={{ use_authorizing: true }}
            selector={({ auth: { isAuth }, onboarding: { selectedMyApps } }) =>
              !isAuth && !!selectedMyApps.length
            }
            key={index}
          >
            <Wrapper className={clsx('swimlane', className)}>
              <BannerSaveYourApps analyticsLocation={analyticsLocation} />
            </Wrapper>
          </Guard>
        );
      default:
        return null;
    }
  });
};

Swimlanes.defaultProps = {
  className: '',
  data: {},
  loadings: {},
  loadingType: 'content', // content || app || service
  loadingRows: 1,
  handleUpdateContentStatus: () => {},
  handleUpdateAppStatus: () => {},
  onSelectClick: () => {},
  swimlanesShift: 0,
  stylesType: 'collections',
  hideTitle: false,
  hideMobileNav: false,
  bannerAddAppsProps: {},
};

Swimlanes.propTypes = {
  className: PropTypes.string,
  data: PropTypes.object,
  loadings: PropTypes.object,
  loadingType: PropTypes.string,
  loadingRows: PropTypes.number,
  analyticsLocation: PropTypes.string.isRequired,
  handleUpdateContentStatus: PropTypes.func,
  handleUpdateAppStatus: PropTypes.func,
  onSelectClick: PropTypes.func,
  swimlanesShift: PropTypes.number,
  stylesType: PropTypes.string,
  cardType: PropTypes.string,
  viewType: PropTypes.string,
  bannerAddAppsProps: PropTypes.object,
};

export default React.memo(SwimlanesHOC);
